import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { Badge } from '../common/UIComponents';
import StarRatingDisplay from '../common/StarRatingDisplay';
import ServiceProviderImage from '../shared/ServiceProviderImage';
import ProfileImageLightbox from './ProfileImageLightbox';
import { ServiceProvider } from '../../types/firestore';
import {
  resolveServiceProviderImageStoragePath,
  resolveServiceProviderImageUrl,
} from '../../utils/serviceProviderImage';

type HeroVariant = 'hero' | 'compact';

interface ServiceProviderProfileHeroProps {
  profile: ServiceProvider;
  reviewsCount: number;
  averageRating: number;
  variant?: HeroVariant;
  className?: string;
  children?: React.ReactNode;
}

const formatReviewCountLabel = (count: number): string => {
  if (count === 1) return 'opinia';
  if (count < 5) return 'opinie';
  return 'opinii';
};

const variantTokens: Record<
  HeroVariant,
  {
    imageContainer: string;
    title: string;
    ratingSize: number;
  }
> = {
  hero: {
    imageContainer: 'h-20 w-20 sm:h-24 sm:w-24 md:h-28 md:w-28',
    title: 'text-2xl sm:text-3xl',
    ratingSize: 18,
  },
  compact: {
    imageContainer: 'h-16 w-16 sm:h-20 sm:w-20',
    title: 'text-lg sm:text-xl md:text-2xl',
    ratingSize: 16,
  },
};

const ServiceProviderProfileHero: React.FC<ServiceProviderProfileHeroProps> = ({
  profile,
  reviewsCount,
  averageRating,
  variant = 'hero',
  className = '',
  children,
}) => {
  const imageSrc = resolveServiceProviderImageUrl(profile, 'full');
  const imageStoragePath = resolveServiceProviderImageStoragePath(profile, 'full');
  const [recoveredImageSrc, setRecoveredImageSrc] = useState<string | null>(null);
  const [imageRenderFailed, setImageRenderFailed] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const imageButtonRef = useRef<HTMLButtonElement | null>(null);
  const address = [profile.address.street, `${profile.address.postalCode} ${profile.address.city}`]
    .filter((value) => String(value || '').trim().length > 0)
    .join(', ');
  const tokens = variantTokens[variant];
  const hasReviews = reviewsCount > 0;
  const canonicalFullImage = profile.profileImage?.full;
  const hasCanonicalFullImage =
    profile.isTestData !== true &&
    Boolean(
      canonicalFullImage &&
        (String(canonicalFullImage.url || '').trim() ||
          String(canonicalFullImage.storagePath || '').trim())
    );
  const lightboxImageSrc = recoveredImageSrc || imageSrc;
  const hasImageLightboxTrigger = hasCanonicalFullImage && Boolean(lightboxImageSrc);
  const canOpenImageLightbox = hasImageLightboxTrigger && !imageRenderFailed;
  const imageDiagnosticContext = useMemo(
    () => ({
      component: 'ServiceProviderProfileHero',
      providerId: profile.uid,
      variant: 'full' as const,
    }),
    [profile.uid]
  );

  useEffect(() => {
    setRecoveredImageSrc(null);
    setImageRenderFailed(false);
    setIsLightboxOpen(false);
  }, [imageSrc, imageStoragePath, profile.uid]);

  const handleRecoveredImageSrc = useCallback((url: string) => {
    setRecoveredImageSrc(url);
    setImageRenderFailed(false);
  }, []);

  const handleImageFallback = useCallback(() => {
    setImageRenderFailed(true);
    setIsLightboxOpen(false);
  }, []);

  const imageElement = (
    <ServiceProviderImage
      src={imageSrc}
      storagePath={imageStoragePath}
      alt={profile.serviceName}
      isTestData={profile.isTestData === true}
      className="block h-full w-full object-cover"
      loading="eager"
      diagnosticContext={imageDiagnosticContext}
      onRecoveredSrc={handleRecoveredImageSrc}
      onFallback={handleImageFallback}
    />
  );

  return (
    <section className={`space-y-3 sm:space-y-4 ${className}`}>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 sm:gap-5">
        {hasImageLightboxTrigger ? (
          <button
            ref={imageButtonRef}
            type="button"
            disabled={!canOpenImageLightbox}
            onClick={() => {
              if (canOpenImageLightbox) {
                setIsLightboxOpen(true);
              }
            }}
            className={`rs-profile-image group shrink-0 overflow-hidden text-left transition-colors focus-outline ${canOpenImageLightbox ? 'hover:brightness-105' : 'cursor-default'} ${tokens.imageContainer}`}
            aria-label={`Powieksz zdjecie profilu: ${profile.serviceName}`}
            title="Powieksz zdjecie profilu"
          >
            {imageElement}
          </button>
        ) : (
          <div className={`rs-profile-image shrink-0 overflow-hidden ${tokens.imageContainer}`}>
            {imageElement}
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1.5 sm:space-y-2">
            <h1
              className={`break-words font-bold leading-tight text-primary-light-100 ${tokens.title}`}
              title={profile.serviceName}
            >
              {profile.serviceName}
            </h1>

            <p
              className="flex items-start gap-1.5 text-sm text-primary-light-400 [overflow-wrap:anywhere]"
              title={address}
            >
              <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                {address}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <StarRatingDisplay rating={averageRating} size={tokens.ratingSize} />
            {hasReviews ? (
              <span className="text-xs text-primary-light-300">
                {averageRating.toFixed(1)}/5 ({reviewsCount} {formatReviewCountLabel(reviewsCount)})
              </span>
            ) : (
              <span className="text-xs italic text-primary-light-400">Brak opinii</span>
            )}
            {hasReviews && (
              <Badge variant="success" size="sm">
                Zweryfikowane opinie
              </Badge>
            )}
            {profile.isTestData === true && (
              <Badge variant="neutral" size="sm">
                Profil testowy - atrapa
              </Badge>
            )}
          </div>
        </div>
      </div>

      {children && (
        <div className="border-t border-primary-dark-300/18 pt-3 sm:pt-4 md:ml-[calc(7rem+1.25rem)]">
          {children}
        </div>
      )}

      <ProfileImageLightbox
        isOpen={isLightboxOpen}
        imageSrc={lightboxImageSrc}
        alt={profile.serviceName}
        onClose={() => {
          setIsLightboxOpen(false);
          window.requestAnimationFrame(() => imageButtonRef.current?.focus());
        }}
      />
    </section>
  );
};

export default ServiceProviderProfileHero;
